import { useEffect, useMemo, useState } from "react";
import Modal from "../../../../components/fundamentals/modal";
import Spinner from "../../../../components/fundamentals/spinner";
import suppliersService from "../../../../services/suppliers.service";
import { Supplier } from "../../../../models/Supplier";

interface IProps {
  onClose: () => void;
  onPick: (supplier: Supplier) => void;
  title?: string;
}

/**
 * RAG-49 — Sélecteur de fournisseur : liste déroulante recherchable (au lieu de
 * saisir un code fournisseur). Alimenté par la liste des fournisseurs (RAG-29).
 */
export default function SupplierPicker({ onClose, onPick, title = "Ajouter tous les produits d'un fournisseur" }: IProps) {
  const [term, setTerm] = useState("");
  const [all, setAll] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { suppliers } = await suppliersService.list();
        if (alive) setAll(suppliers);
      } catch (e) {
        if (alive) setAll([]);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, []);

  const results = useMemo(() => {
    const t = term.trim().toLowerCase();
    if (!t) return all;
    return all.filter((s) => s.provider.toLowerCase().includes(t));
  }, [term, all]);

  return (
    <Modal handleClose={onClose} isLargeModal>
      <Modal.Body>
        <Modal.Header handleClose={onClose}>
          <h1 className="inter-xlarge-semibold">{title}</h1>
        </Modal.Header>
        <Modal.Content>
          <input
            autoFocus
            className="bg-grey-5 border border-gray-20 rounded-rounded h-10 w-full px-small outline-none focus:border-blue-60"
            placeholder="Rechercher un fournisseur par son code (ex : 3M)..."
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />

          {loading && (
            <div className="py-base flex justify-center">
              <Spinner />
            </div>
          )}

          {!loading && results.length === 0 && (
            <p className="py-base text-grey-50 inter-small-regular">Aucun fournisseur trouvé.</p>
          )}

          <div className="mt-base max-h-[320px] overflow-y-auto flex flex-col gap-y-1">
            {results.map((supplier) => (
              <button
                key={supplier.provider}
                type="button"
                className="flex items-center justify-between text-left px-small py-2 rounded-rounded hover:bg-grey-10"
                onClick={() => onPick(supplier)}
              >
                <span className="inter-base-regular text-grey-90">{supplier.provider}</span>
                <span className="inter-small-regular text-grey-50">{supplier.products_count} produit(s)</span>
              </button>
            ))}
          </div>
        </Modal.Content>
      </Modal.Body>
    </Modal>
  );
}
